import { Location } from '@angular/common';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { DestroyRef, effect, inject, Injectable, signal, untracked, WritableSignal } from '@angular/core';
import { Params, Router } from '@angular/router';

// Registered here rather than in an app's providers: without the plugin the strict argument is
// ignored and `2026-02-30` validates, so a consumer that never runs an app bootstrap — a spec,
// another library — would lose the check with nothing to notice.
dayjs.extend(customParseFormat);

/**
 * Translates a query parameter's raw string into a signal value and back. `parse` returns
 * `undefined` for a value the parameter cannot carry, `serialize` for a value the URL
 * should omit — the default selection, an empty list, an empty search term.
 */
export interface UrlQueryParamCodec<TValue> {
  readonly parse: (rawValue: string) => TValue | undefined;
  readonly serialize: (value: TValue) => string | undefined;
}

/** One entry of a {@link urlQueryParamVocabulary} — the value the app holds, and its name in the URL. */
export interface UrlQueryParamVocabularyEntry<TValue> {
  readonly value: TValue;
  readonly name: string;
}

/** A plain text parameter; an empty string is dropped from the URL. */
export const urlQueryParamString: UrlQueryParamCodec<string> = {
  parse: (rawValue) => rawValue || undefined,
  serialize: (value) => value || undefined,
};

/** A comma-separated list, as the multi-select filters carry it. */
export const urlQueryParamStringList: UrlQueryParamCodec<string[]> = {
  parse: (rawValue) => {
    const entries = rawValue.split(',').filter((entry) => entry.length > 0);
    return entries.length ? entries : undefined;
  },
  serialize: (value) => (value.length ? value.join(',') : undefined),
};

/**
 * A comma-separated list of numeric ids, as the id-based multi-select filters carry it.
 *
 * An entry that is not a plain integer is dropped rather than passed on as `NaN`, so a
 * hand-edited link cannot reach an endpoint as a value it rejects. `Number.isSafeInteger`
 * covers the rest: `9007199254740993` matches the pattern and parses to a different id.
 */
export const urlQueryParamNumberList: UrlQueryParamCodec<number[]> = {
  parse: (rawValue) => {
    const values = rawValue
      .split(',')
      .filter((entry) => /^\d+$/.test(entry))
      .map((entry) => Number.parseInt(entry, 10))
      .filter((value) => Number.isSafeInteger(value));
    return values.length ? values : undefined;
  },
  serialize: (value) => (value.length ? value.join(',') : undefined),
};

/**
 * A single ISO 8601 date (`yyyy-mm-dd`), as `dm-date-picker` reads and writes it.
 *
 * The calendar is checked, not only the shape: `2026-02-30` matches `yyyy-mm-dd` and is not a
 * day that exists, so it would sit in the URL and travel to an endpoint that can only reject
 * it. Strict parsing also rejects a wrong shape — `2026-1-1`, `20260101` — so the format is
 * the whole contract. An empty string clears the parameter, which is what the picker's own
 * clear affordance reports.
 *
 * Dates before year 100 are refused. `dayjs` parses through `Date`, which maps a year of
 * 0-99 to 1900-1999, and strict mode reports the mismatch it causes rather than the date.
 * Out of range for a filter nobody sets to the first century.
 */
const ISO_DATE_FORMAT = 'YYYY-MM-DD';

const isCalendarDate = (value: string): boolean => dayjs(value, ISO_DATE_FORMAT, true).isValid();

export const urlQueryParamIsoDate: UrlQueryParamCodec<string> = {
  parse: (rawValue) => (isCalendarDate(rawValue) ? rawValue : undefined),
  serialize: (value) => (isCalendarDate(value) ? value : undefined),
};

/**
 * A closed vocabulary spelled out in the URL: the signal keeps the value the rest of the app
 * and the API use, the URL carries a readable name for it. One neutral spelling in every
 * language — the name is not translated.
 *
 * @param {readonly UrlQueryParamVocabularyEntry<TValue>[]} entries - The vocabulary
 * @returns {UrlQueryParamCodec<TValue>} The codec
 */
export function urlQueryParamVocabulary<TValue>(entries: readonly UrlQueryParamVocabularyEntry<TValue>[]): UrlQueryParamCodec<TValue> {
  return {
    parse: (rawValue) => entries.find((entry) => entry.name === rawValue)?.value,
    serialize: (value) => entries.find((entry) => entry.value === value)?.name,
  };
}

/**
 * The names a selection is spelled with, which is what a `dm-*` panel binds its `value` to.
 *
 * A panel speaks in the vocabulary's names while the signal behind it keeps typed values, so
 * every vocabulary-backed control needs this translation in both directions — see
 * {@link vocabularyValuesOf} for the way back.
 *
 * @param {readonly UrlQueryParamVocabularyEntry<TValue>[]} entries - The vocabulary
 * @param {readonly TValue[]} values - The current selection
 * @returns {string[]} Their names, in the vocabulary's order
 */
export function vocabularyNamesOf<TValue>(entries: readonly UrlQueryParamVocabularyEntry<TValue>[], values: readonly TValue[]): string[] {
  return entries.filter((entry) => values.includes(entry.value)).map((entry) => entry.name);
}

/**
 * The single-select counterpart of {@link vocabularyNamesOf}.
 *
 * @param {readonly UrlQueryParamVocabularyEntry<TValue>[]} entries - The vocabulary
 * @param {TValue} value - The current selection, or `undefined` while unset
 * @returns {string} Its name, empty while unset
 */
export function vocabularyNameOf<TValue>(entries: readonly UrlQueryParamVocabularyEntry<TValue>[], value: TValue): string {
  return entries.find((entry) => entry.value === value)?.name ?? '';
}

/**
 * The values behind the names a panel reported. A name the vocabulary does not carry is
 * dropped, so a stale option cannot reach the request.
 *
 * @param {readonly UrlQueryParamVocabularyEntry<TValue>[]} entries - The vocabulary
 * @param {readonly string[]} names - The names the panel reported
 * @returns {TValue[]} Their values, in the vocabulary's order
 */
export function vocabularyValuesOf<TValue>(entries: readonly UrlQueryParamVocabularyEntry<TValue>[], names: readonly string[]): TValue[] {
  return entries.filter((entry) => names.includes(entry.name)).map((entry) => entry.value);
}

/**
 * The multi-select counterpart of {@link urlQueryParamVocabulary}. Unknown entries are dropped.
 *
 * @param {readonly UrlQueryParamVocabularyEntry<TValue>[]} entries - The vocabulary
 * @returns {UrlQueryParamCodec<TValue[]>} The codec
 */
export function urlQueryParamVocabularyList<TValue>(
  entries: readonly UrlQueryParamVocabularyEntry<TValue>[],
): UrlQueryParamCodec<TValue[]> {
  return {
    parse: (rawValue) => {
      const values = rawValue
        .split(',')
        .map((rawEntry) => entries.find((entry) => entry.name === rawEntry)?.value)
        .filter((value): value is TValue => value !== undefined);
      return values.length ? values : undefined;
    },
    serialize: (value) => {
      const names = value
        .map((entry) => entries.find((vocabularyEntry) => vocabularyEntry.value === entry)?.name)
        .filter((name): name is string => name !== undefined);
      return names.length ? names.join(',') : undefined;
    },
  };
}

/** Write timing of a {@link urlQueryParamSignal}. */
export interface UrlQueryParamOptions {
  /**
   * Holds the URL write back while this returns `false` — for a value the user edits in a
   * transient surface, such as a filter panel that applies its selection on close. The
   * value reached meanwhile is written as soon as it returns `true` again.
   */
  readonly writeWhen?: () => boolean;
}

/**
 * A writable signal backed by a URL query parameter: it starts from what the URL carries
 * and writes every later value back through the Router, replacing the current history entry,
 * so a reload restores the state and the browser history stays free of filter noise.
 *
 * Writing through the Router keeps the URL tree that `queryParamsHandling` reads current.
 *
 * Parameters not owned by the signal are left untouched, so several of them — and several
 * components — can share one URL. A value the codec serializes to `undefined` removes the
 * parameter instead of writing an empty one, and the default value stays out of the URL
 * until the user moves away from it. A value the codec rejects is corrected in the URL, so
 * a copied link never names a state the page does not show.
 *
 * Call from an injection context, next to the other signal fields:
 *
 * ```ts
 * protected readonly activeScope = urlQueryParamSignal<Scope>('scope', 'meine', urlQueryParamOneOf(SCOPES));
 * ```
 *
 * @param {string} parameterName - Name of the query parameter
 * @param {TValue} defaultValue - Value used when the URL carries nothing usable; never written to the URL by itself
 * @param {UrlQueryParamCodec<TValue>} codec - Translation between the value and its raw string
 * @param {UrlQueryParamOptions} options - Optional write timing
 * @returns {WritableSignal<TValue>} The signal, already carrying the URL's value
 */
export function urlQueryParamSignal<TValue>(
  parameterName: string,
  defaultValue: TValue,
  codec: UrlQueryParamCodec<TValue>,
  options: UrlQueryParamOptions = {},
): WritableSignal<TValue> {
  const location = inject(Location);
  const writer = inject(UrlQueryParamWriter);
  const writerOwner = {};
  inject(DestroyRef).onDestroy(() => writer.discardPendingWrites(writerOwner));

  const rawValue = readQueryParams(location).get(parameterName);
  const parsedValue = rawValue === null ? undefined : codec.parse(rawValue);
  const initialValue = parsedValue ?? defaultValue;
  const valueSignal = signal<TValue>(initialValue);

  // True when the URL spells the parameter differently from what this page writes.
  let hasLeftInitialValue = rawValue !== null && rawValue !== codec.serialize(initialValue);

  effect(() => {
    const value = valueSignal();
    const mayWrite = options.writeWhen?.() ?? true;

    untracked(() => {
      if (!mayWrite || (!hasLeftInitialValue && value === initialValue)) {
        return;
      }
      hasLeftInitialValue = true;
      writer.write(parameterName, codec.serialize(value), writerOwner);
    });
  });

  return valueSignal;
}

/**
 * The `info` of every navigation a {@link urlQueryParamSignal} starts to write its parameters, so
 * a listener that reacts to navigations can tell a filter write from the user going somewhere.
 */
export const URL_QUERY_PARAM_WRITE = Symbol('urlQueryParamWrite');

/** A write waiting for its navigation, and the {@link urlQueryParamSignal} that made it. */
interface PendingQueryParamWrite {
  readonly serializedValue: string | undefined;
  readonly owner: object;
  /** Set once the write is put back after a navigation that did not complete. */
  readonly isRetry?: boolean;
}

/**
 * Applies the writes of every {@link urlQueryParamSignal} made in one tick as a single
 * `replaceUrl` navigation, so concurrent writes cannot drop each other. Writes wait while
 * another navigation runs, which starting one would cancel, and are discarded if their signal
 * is destroyed meanwhile. A write whose navigation does not complete is put back once.
 */
@Injectable({ providedIn: 'root' })
class UrlQueryParamWriter {
  private readonly router = inject(Router);

  private readonly pendingWrites = signal<ReadonlyMap<string, PendingQueryParamWrite>>(new Map());
  private readonly destroyedOwners = new WeakSet<object>();

  private readonly flushWhenIdleEffect = effect(() => {
    const pendingWrites = this.pendingWrites();
    if (!pendingWrites.size || this.router.currentNavigation() !== null) {
      return;
    }

    untracked(() => this.flush(pendingWrites));
  });

  /**
   * @param {string} parameterName - Name of the query parameter
   * @param {string | undefined} serializedValue - The value to write, `undefined` to remove the parameter
   * @param {object} owner - The signal making the write, for {@link discardPendingWrites}
   */
  public write(parameterName: string, serializedValue: string | undefined, owner: object): void {
    this.pendingWrites.update((pendingWrites) => new Map(pendingWrites).set(parameterName, { serializedValue, owner }));
  }

  /**
   * @param {object} owner - The signal being destroyed
   */
  public discardPendingWrites(owner: object): void {
    this.destroyedOwners.add(owner);
    this.pendingWrites.update((pendingWrites) => new Map([...pendingWrites].filter(([, pendingWrite]) => pendingWrite.owner !== owner)));
  }

  /**
   * @param {ReadonlyMap<string, PendingQueryParamWrite>} pendingWrites - The writes to apply
   */
  private flush(pendingWrites: ReadonlyMap<string, PendingQueryParamWrite>): void {
    this.pendingWrites.set(new Map());

    const currentQueryParams = this.router.routerState.snapshot.root.queryParamMap;
    const changedQueryParams: Params = {};
    for (const [parameterName, { serializedValue }] of pendingWrites) {
      if ((currentQueryParams.get(parameterName) ?? undefined) !== serializedValue) {
        changedQueryParams[parameterName] = serializedValue ?? null;
      }
    }
    if (!Object.keys(changedQueryParams).length) {
      return;
    }

    this.router
      .navigate([], {
        queryParams: changedQueryParams,
        queryParamsHandling: 'merge',
        preserveFragment: true,
        replaceUrl: true,
        info: URL_QUERY_PARAM_WRITE,
      })
      .then(
        (hasNavigated) => {
          if (!hasNavigated) {
            this.retryFailedWrites(pendingWrites);
          }
        },
        () => this.retryFailedWrites(pendingWrites),
      );
  }

  /**
   * Puts back the writes of a navigation that did not complete — once, so a navigation that is
   * always refused cannot loop — unless a newer write replaced them or their signal is destroyed.
   *
   * @param {ReadonlyMap<string, PendingQueryParamWrite>} failedWrites - The writes of that navigation
   */
  private retryFailedWrites(failedWrites: ReadonlyMap<string, PendingQueryParamWrite>): void {
    this.pendingWrites.update((pendingWrites) => {
      const retriedWrites = new Map(pendingWrites);
      for (const [parameterName, failedWrite] of failedWrites) {
        if (failedWrite.isRetry || retriedWrites.has(parameterName) || this.destroyedOwners.has(failedWrite.owner)) {
          continue;
        }
        retriedWrites.set(parameterName, { ...failedWrite, isRetry: true });
      }
      return retriedWrites;
    });
  }
}

/**
 * @param {Location} location - The current location
 * @returns {URLSearchParams} The query string of the current URL, parsed
 */
function readQueryParams(location: Location): URLSearchParams {
  const pathWithoutHash = location.path(false);
  const queryIndex = pathWithoutHash.indexOf('?');
  return new URLSearchParams(queryIndex === -1 ? '' : pathWithoutHash.slice(queryIndex + 1));
}
